import React, { useState } from 'react';
import { apiFetch } from '../constants';

const CATEGORIES = [
  { value: 'help', label: 'Help / technical issue' },
  { value: 'service', label: 'Service or training request' },
  { value: 'other', label: 'Other request' },
];

export default function ClientSupportForm({ onClose, onSuccess, userEmail = '', userName = '' }) {
  const [category, setCategory] = useState('help');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/client-requests', {
        method: 'POST',
        body: JSON.stringify({
          category,
          subject: subject.trim(),
          message: message.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send request');
      }
      onSuccess?.(data.message || 'Request sent.');
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-lg bg-gray-950 border border-teal-500/30 rounded-xl p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-teal-400">Help &amp; Requests</h2>
            <p className="text-sm text-gray-400 mt-1">
              Send a message to the platform team for help, services, or other requests.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-sm"
          >
            Close
          </button>
        </div>

        {(userEmail || userName) && (
          <p className="text-xs text-gray-500 mb-4">
            Sending as {userName ? `${userName} · ` : ''}{userEmail || 'your account'}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
              disabled={loading}
            >
              {CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              maxLength={200}
              placeholder="Brief summary of your request"
              className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Message</label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              maxLength={5000}
              placeholder="Describe what you need help with or what you're requesting..."
              className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white resize-none"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !subject.trim() || !message.trim()}
              className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg"
            >
              {loading ? 'Sending…' : 'Send Request'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-white"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
